import React, {
  useState,
  useEffect,
  useCallback,
  useRef,
  useImperativeHandle,
  forwardRef,
} from 'react';
import { ReactComponent as Error } from 'assets/svg/error.svg';
import { ReactComponent as Success } from 'assets/svg/success.svg';
import ModalGeneric from '../ModalGeneric/ModalGeneric';
import classNames from 'classnames';
import DOMPurify from 'dompurify';
import './AlertModal.scss';

const NOOP = () => {};

export default forwardRef(function AlertModal(
  { timer = 10000, onClose = () => {} },
  ref,
) {
  const [open, setOpen] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState(false);
  const timeout = useRef();
  const closeCallback = useRef(NOOP);

  const Icon = error ? Error : Success;

  const closeHandler = useCallback(() => {
    clearInterval(timeout.current);
    setMessage(false);
    setError(false);
    setOpen(false);
    closeCallback.current();
    onClose();
  }, [onClose]);

  const openHandler = useCallback((msg, callback, err) => {
    clearInterval(timeout.current);
    setError(err);
    setMessage(msg);
    closeCallback.current = callback;
    setOpen(true);
  }, []);

  useEffect(() => {
    if (open) {
      timeout.current = setTimeout(closeHandler, timer);
      return () => {
        clearInterval(timeout.current);
      };
    }
  }, [open, closeHandler, timer]);

  useImperativeHandle(ref, () => ({
    error: (msg, callback = NOOP) => openHandler(msg, callback, true),
    message: (msg, callback = NOOP) => openHandler(msg, callback, false),
    hide: closeHandler,
  }));

  return (
    <ModalGeneric
      open={open}
      onModalClose={closeHandler}
      onClick={closeHandler}
      className="alert-modal"
    >
      <Icon className={classNames('icon', { error })} />
      <div className="message">
        <span
          dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(message) }}
        />
      </div>
    </ModalGeneric>
  );
});
